import { useState } from 'react';

import { ErrorCode } from '@constants/errorCodes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { getApiErrorMessage } from '@helpers/apiError';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';

import { MoveToCofeeAction } from './constants';
import {
  getNameFieldErrorMessage,
  parseMoveToCofeeResponse
} from './responseHelpers';
import {
  AddToMemberListFormData,
  MoveToCofeeContact,
  QuickCollectFormData,
  QuickCollectGroupFormData
} from './types';
import { isLeadPhoneUnchanged } from './validation';

import { getDefaultBranchMember } from '../../helper';
import {
  useLeadAddToBranchMutation,
  useLeadAddToGroupMutation,
  useLeadQuickCollectMutation
} from '../../queries/LeadManagementQuery';
import { LeadType } from '../../types/common';

const getCapitalizedApiErrorMessage = (error: unknown): string | undefined => {
  const message = getApiErrorMessage(error);
  return message ? capitalizeFirstLetter(message) : undefined;
};

const getAddMemberErrorMessage = (error: unknown, fallback: string): string => {
  const errorCode = (error as ApiError)?.data?.errorCode;
  if (errorCode === ErrorCode.ERR_MEMBER_ALREADY_EXISTS) {
    return 'This lead already exist in the selected branch.';
  }
  return getCapitalizedApiErrorMessage(error) || fallback;
};

interface UseMoveToCofeeSubmitArgs {
  leadData: LeadType;
  selectedAction: MoveToCofeeAction | null;
  memberListForm: AddToMemberListFormData;
  quickCollectForm: QuickCollectFormData;
  quickCollectGroupForm: QuickCollectGroupFormData;
  onClose: () => void;
  onSuccess: () => void;
  onNameError: (message: string) => void;
  onUpdateLeadPhone: (
    mobileNumber: string,
    countryCode: string
  ) => Promise<boolean>;
}

const useMoveToCofeeSubmit = ({
  leadData,
  selectedAction,
  memberListForm,
  quickCollectForm,
  quickCollectGroupForm,
  onClose,
  onSuccess,
  onNameError,
  onUpdateLeadPhone
}: UseMoveToCofeeSubmitArgs) => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  // Notify the parent that the move succeeded so it can optimistically mark the
  // lead as onboarded, then alert and close.
  const handleSuccess = (message: string) => {
    onSuccess();
    createSuccessAlert(message);
    onClose();
  };

  const handleApiError = (message?: string) => {
    createErrorAlert(message);
    onClose();
  };

  const handleApiFailure = (error: unknown, fallbackMessage: string) => {
    const nameMessage = getNameFieldErrorMessage(error);
    if (nameMessage) {
      onNameError(nameMessage);
      return;
    }
    handleApiError(fallbackMessage);
  };

  const handleApiResult = (
    responseBody: unknown,
    successMessage: string,
    fallbackErrorMessage: string
  ) => {
    const result = parseMoveToCofeeResponse(responseBody);
    if (result.type === 'success') {
      handleSuccess(successMessage);
    } else if (result.type === 'nameError') {
      onNameError(result.message);
    } else {
      handleApiError(result.message || fallbackErrorMessage);
    }
  };

  const [leadAddToBranch, { isLoading: isAddingToBranch }] =
    useLeadAddToBranchMutation();
  const [leadAddToGroup, { isLoading: isAddingToGroup }] =
    useLeadAddToGroupMutation();
  const [leadQuickCollect, { isLoading: isQuickCollecting }] =
    useLeadQuickCollectMutation();

  const isSubmitting =
    isUpdatingPhone || isAddingToBranch || isAddingToGroup || isQuickCollecting;

  const submitAddToMemberList = async () => {
    const branchMember = getDefaultBranchMember(
      memberListForm.name,
      memberListForm.mobileNumber,
      memberListForm.countryCode
    );

    if (memberListForm.assignToGroup && memberListForm.groupId) {
      const res = await leadAddToGroup({
        params: {
          organizationId,
          branchId: memberListForm.branchId,
          groupId: memberListForm.groupId,
          leadId: leadData.id
        },
        body: {
          members_added: [
            {
              name: memberListForm.name,
              mobile: memberListForm.mobileNumber,
              country_code: memberListForm.countryCode,
              amount: null
            }
          ],
          force_create: true
        }
      });
      if ('data' in res) {
        handleApiResult(
          res.data,
          'Lead added to group successfully',
          'Failed to add lead to group'
        );
      } else {
        handleApiFailure(
          res.error,
          getAddMemberErrorMessage(res.error, 'Failed to add lead to group')
        );
      }
    } else {
      const res = await leadAddToBranch({
        params: {
          organizationId,
          branchId: memberListForm.branchId,
          leadId: leadData.id
        },
        body: {
          branch_members: [branchMember],
          force_create: true
        }
      });
      if ('data' in res) {
        handleApiResult(
          res.data,
          'Lead added to branch successfully',
          'Failed to add lead to branch'
        );
      } else {
        handleApiFailure(
          res.error,
          getAddMemberErrorMessage(res.error, 'Failed to add lead to branch')
        );
      }
    }
  };

  const submitQuickCollect = async () => {
    const res = await leadQuickCollect({
      organizationId,
      leadId: leadData.id,
      branchId: quickCollectForm.branchId,
      body: {
        amount: +quickCollectForm.amount,
        note: quickCollectForm.notes.trim(),
        member_details: [
          {
            name: quickCollectForm.name,
            mobile: quickCollectForm.mobileNumber,
            country_code: quickCollectForm.countryCode
          }
        ],
        send_payment_links: true
      }
    });
    if ('data' in res) {
      handleApiResult(
        res.data,
        'Payment link sent successfully',
        'Failed to send payment link'
      );
    } else {
      handleApiFailure(
        res.error,
        getCapitalizedApiErrorMessage(res.error) ||
          'Failed to send payment link'
      );
    }
  };

  const submitQuickCollectGroup = async () => {
    const form = quickCollectGroupForm;
    const quickCollectBody: Record<string, any> = {
      amount: +form.amount,
      note: form.notes.trim(),
      member_details: [
        {
          name: form.name,
          mobile: form.mobileNumber,
          country_code: form.countryCode
        }
      ],
      send_payment_links: true,
      group_id: form.groupId
    };

    const res = await leadQuickCollect({
      organizationId,
      leadId: leadData.id,
      branchId: form.branchId,
      body: quickCollectBody
    });
    if ('data' in res) {
      handleApiResult(
        res.data,
        'Payment link sent successfully',
        'Failed to send payment link'
      );
    } else {
      handleApiFailure(
        res.error,
        getCapitalizedApiErrorMessage(res.error) ||
          'Failed to send payment link'
      );
    }
  };

  // Returns false when the lead phone could not be saved (the caller already
  // shows the error), so the move is not attempted with a mismatched phone.
  const syncLeadPhone = async (contact: MoveToCofeeContact) => {
    if (isLeadPhoneUnchanged(contact, leadData)) return true;
    setIsUpdatingPhone(true);
    try {
      return await onUpdateLeadPhone(contact.mobileNumber, contact.countryCode);
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
      if (await syncLeadPhone(memberListForm)) submitAddToMemberList();
    } else if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
      if (await syncLeadPhone(quickCollectForm)) submitQuickCollect();
    } else if (
      selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP
    ) {
      if (await syncLeadPhone(quickCollectGroupForm)) {
        submitQuickCollectGroup();
      }
    }
  };

  return { handleSubmit, isSubmitting };
};

export default useMoveToCofeeSubmit;
